import { makeAutoObservable } from 'mobx';
import { amountViewToValue } from 'src/components/CustomersBar/CustomerTab/BankingTab/components/amountViewToValue';
import { billingInfoFields, SetDepositFinalViewProps } from 'src/components/TopUpProcedure/RealexForm/helper';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { SdkStaff, StaffAccountBasicDataType, StaffAccountWalletDataType } from 'src/AppState/sdk/staff';
import { ApiResponseWrapper } from 'src/utils-mobx/ApiResponseWrapper';
import { Amount } from 'src_common/common/amount/Amount';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { CurrencyType, moneySymbol } from 'src_common/common/amount/website-money/currency';
//@ts-expect-error
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Common } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { validateMinAmount } from 'src/domains/players/shared/validatorsNew/validateMinMaxAmount';
import {
    validateAmountPattern,
    validateAmountRequire,
    validatePositive,
} from 'src/domains/players/shared/validatorsNew/validateRequire';

interface FormParamsType {
    formActionUrl: string;
    formData: {
        kind: 'realex';
        data: Record<string, string> | null;
    };
    type: string;
}
export class RealexTabState {
    public errorMessage: string | null = null;
    public successMessage: boolean = false;
    public isIframeOpen = false;
    public formParams: FormParamsType | undefined = undefined;

    public readonly depositAmount: FormInputState<string, Amount>;
    private configState: ConfigState;
    public readonly basicData: () => ApiResponseWrapper<StaffAccountBasicDataType | null>;
    public readonly walletData: () => ApiResponseWrapper<StaffAccountWalletDataType | null>;

    public constructor(
        private sdkStaff: SdkStaff,
        private models: ModelsState,
        private customerId: number,
        private permissionGrants: ImmutablePropTypes.map,
        private common: Common,
        private onQueryAudits: () => void
    ) {
        this.configState = ConfigState.get(this.common);
        this.depositAmount = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validatePositive)
            .map(validateAmountPattern())
            .map(
                validateMinAmount(
                    new Amount(this.configState.config.minDepositAmount),
                    () => moneySymbol(this.getCurrency),
                    'Minimum deposit amount is'
                )
            );

        this.basicData = (): ApiResponseWrapper<StaffAccountBasicDataType | null> =>
            this.models.getUserModel(this.customerId).basicData;
        this.walletData = (): ApiResponseWrapper<StaffAccountWalletDataType | null> =>
            this.models.getUserModel(this.customerId).walletData;

        this.areMissingFieldsForBillingInfoRealex();
        makeAutoObservable(this);
    }

    public areMissingFieldsForBillingInfoRealex = (): boolean => {
        const basicData = this.basicData().data;
        if (basicData === null) {
            return true;
        }

        const billingInfo = billingInfoFields(basicData);
        if (billingInfo.type === 'error') {
            this.createErrorMessage('missing-fields', billingInfo.fieldsRequired.join(', '));

            return true;
        }

        return false;
    };

    public onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const inputValue = e.target.value;
        const formatValue = amountViewToValue(inputValue);
        this.depositAmount.setValue(formatValue);
    };

    public handleAdditionsChange = (newAmount: Amount): void => {
        this.depositAmount.setValue(newAmount.value);
        this.depositAmount.setAsVisited();
    };

    public handleClear = (): void => {
        this.depositAmount.setValue('');
        this.depositAmount.setAsVisited();
    };

    public onCancel = (): undefined => {
        this.depositAmount.setValue('');
        this.clearForm();
        this.clearInputs();
        this.isIframeOpen = false;
        return undefined;
    };

    public get isEmptyAmountInput(): boolean {
        return this.depositAmount.result.value.type !== 'ok' || this.depositAmount.value === '';
    }
    public topUpIframe = async (): Promise<void> => {
        this.depositAmount.setAsVisited();
        const amount = $appState.configState.precision.valueOldFormat(new Amount(this.depositAmount.value));
        const basicData = this.basicData().data;

        if (basicData === null) {
            return;
        } else if (this.getPermission()) {
            //disable payment because no permissions
            return;
        }

        const billingInfo = billingInfoFields(basicData);
        if (billingInfo.type !== 'ok') {
            this.createErrorMessage('missing-fields', billingInfo.fieldsRequired.join(', '));
            return;
        }

        const response = await this.sdkStaff.depositInitiate({
            customer_id: this.customerId,
            requestBody: {
                channel: 'backoffice',
                status: 'initiated',
                amount,
                billingInfo: billingInfo.data,
            },
        });

        if (response.status === '200') {
            const { bodyJson } = response;

            const paymentForm = bodyJson.paymentForm ?? false;

            if (bodyJson.status === 'failed') {
                if (bodyJson.reason === 'invalid-data') {
                    this.createErrorMessage('invalid-data');
                } else {
                    this.createErrorMessage('unknown');
                }
            } else if (paymentForm === false) {
                throw 'Payment form is null';
            } else {
                this.formParams = {
                    formActionUrl: paymentForm.formActionUrl,
                    formData: {
                        kind: 'realex',
                        data: paymentForm.formData,
                    },
                    type: paymentForm.type,
                };
                this.isIframeOpen = true;
            }
        } else {
            const { errors } = response;
            const error = errors[0];
            if (error?.code === 'minimum') {
                this.createErrorMessage('minimum');
            } else if (error?.code === 'limit-reached') {
                this.createErrorMessage('limit-reached');
            } else if (error?.code === 'deposits-not-allowed') {
                this.createErrorMessage('deposits-not-allowed', error.debugDetails ?? undefined);
            } else if (error?.code === 'failed-deposit-restriction') {
                this.createErrorMessage('failed-deposit-restriction', error.debugDetails ?? undefined);
            } else if (error?.code === 'limit-deposit-restriction') {
                this.createErrorMessage('limit-deposit-restriction');
            } else if (error?.code === 'backoffice-deposit-restriction') {
                this.createErrorMessage('backoffice-deposit-restriction');
            } else {
                this.createErrorMessage('unknown');
            }
        }
    };

    private createErrorMessage = (errorType: string, message?: string): void => {
        switch (errorType) {
            case 'invalid-data':
                this.errorMessage = 'Some fields are invalid.';
                break;
            case 'minimum':
                this.errorMessage = `Deposit amount can not be lower than defined ${this.configState.config.minDepositAmount} limit`;
                break;
            case 'limit-reached':
                this.errorMessage = "Deposit can't exceed defined limit.";
                break;
            case 'deposits-not-allowed':
                this.errorMessage = message === undefined ? 'Your account is suspended.' : message;
                break;
            case 'declined':
                this.errorMessage = 'ERROR_DEPOSIT_DECLINED';
                break;
            case 'missing-fields':
                if (message === undefined) {
                    return;
                }
                this.errorMessage = `Please add customer ${message} to customer 'Personal account' details.`;
                break;
            case 'errorIframe':
                this.errorMessage = 'Error in transaction final step. More details in Payment Search';
                break;
            case 'unknown':
                this.errorMessage = 'Unknown error';
                break;
            case 'backoffice-deposit-restriction':
            case 'limit-deposit-restriction':
            case 'failed-deposit-restriction':
                this.errorMessage = 'Deposit is not allowed when account is deposit restricted';
                break;
            default:
                this.errorMessage = 'Unknown error happened';
        }
    };

    public clearForm = (): void => {
        this.errorMessage = null;
        this.successMessage = false;
    };

    private clearInputs = (): void => {
        this.depositAmount.setValue('');
    };

    public setDepositFinalView = (value: SetDepositFinalViewProps): void => {
        if (value === 'success') {
            this.successMessage = true;
            this.onQueryAudits();
            return;
        } else if (value === 'declined') {
            this.createErrorMessage('declined');
            this.isIframeOpen = false;
            return;
        }
        this.createErrorMessage('errorIframe');
        this.isIframeOpen = false;
    };

    public get getCurrency(): CurrencyType {
        return this.walletData().data?.currency ?? 'XTS';
    }

    public get getBalance(): Amount {
        return new Amount(this.walletData().data?.balance ?? '0');
    }

    public get getWithdrawableBalance(): Amount {
        return new Amount(this.walletData().data?.withdrawableBalance ?? '0');
    }

    public get newBalance(): Amount {
        try {
            const basicInput =
                this.depositAmount.result.value.type === 'ok' ? this.depositAmount.result.value.data : new Amount('0');
            return this.getBalance.add(basicInput);
        } catch (error) {
            console.error(error);
            return new Amount('0');
        }
    }

    public confirmationTopUp = async (): Promise<void> => {
        this.clearForm();

        await this.topUpIframe();
    };

    public getPermission = (permission = 'banking/deposits'): boolean => {
        if (this.permissionGrants?.size !== undefined) {
            for (let i = 0; i < this.permissionGrants.size; i++) {
                if (this.permissionGrants.getIn([i, 'permission']) === permission) {
                    return false;
                }
            }
        }

        return true;
    };
}
